import { IMAGES, BRAND, waLink } from "@/lib/site";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: "easeOut", delay: 0.1 * i },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <motion.h1
            initial="hidden"
            animate="show"
            custom={1}
            variants={fadeUp}
            className="font-serif font-light text-[58px] sm:text-[80px] lg:text-[112px] leading-[0.95] tracking-tight text-black"
          >
            A arte das
            <br />
            sobrancelhas <em className="italic font-light">naturais</em>.
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            custom={2}
            variants={fadeUp}
            className="mt-8 max-w-xl text-base md:text-lg text-neutral-600 leading-relaxed"
          >
            Aqui o foco é você! Todos os procedimentos são personalizados para
            entregar o resultado mais natural e elegante possível.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            custom={3}
            variants={fadeUp}
            className="mt-10 flex flex-col sm:flex-row gap-3"
          >
            <a
              href={BRAND.bookingUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-book"
              className="group inline-flex items-center justify-center gap-3 bg-black text-white px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-neutral-800 transition-colors"
            >
              Agendar Online
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              className="group inline-flex items-center justify-center gap-3 border border-black text-black px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-black hover:text-white transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            custom={4}
            variants={fadeUp}
            className="mt-16 grid grid-cols-3 gap-6 max-w-md"
          >
            {[
              ["02", "anos de\nexperiência"],
              ["06", "serviços\nexclusivos"],
              ["100%", "natural &\npersonalizado"],
            ].map(([n, l]) => (
              <div key={n} className="border-t border-black/15 pt-4">
                <div className="font-serif text-3xl text-black">{n}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 whitespace-pre-line mt-1">
                  {l}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2 relative">
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="relative aspect-[4/5] w-full bg-neutral-100 overflow-hidden"
            data-testid="hero-portrait"
          >
            <img
              src={IMAGES.heroPortrait}
              alt="Bianca Sena — Brow Designer"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
              <span className="text-white text-[10px] tracking-[0.3em] uppercase drop-shadow">
                Bianca Sena
              </span>
              <span className="text-white text-[10px] tracking-[0.3em] uppercase drop-shadow">
                Nº 01
              </span>
            </div>
          </motion.div>
          <div className="hidden lg:block absolute -bottom-6 -left-6 h-32 w-32 border border-black/20" />
        </div>
      </div>
    </section>
  );
}
